import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Replace with client-provided Q&A content
const FAQS = [
  {
    q: "Do you come to my location?",
    a: "Yes — 100% mobile. We bring everything needed to your driveway, workplace, or wherever your car is parked. You don't need to drop anything off.",
  },
  {
    q: "How long does a detail take?",
    a: "An Express Wash & Shine takes 60–90 minutes. The Signature Detail (interior + exterior) runs 3–5 hours. Ceramic Shield Pro with paint correction is typically a full day or two days depending on vehicle condition.",
  },
  {
    q: "What areas do you serve?",
    a: "We serve three regions: within 2 hours of Levittown, PA; within 1 hour of JBMDL, NJ; and within 1 hour of Lima, OH. Use the service area map on this page to see if you're covered.",
  },
  {
    q: "How do I book an appointment?",
    a: "Use the booking widget on this page to select your service and preferred time. You'll get appointment reminders and an invoice automatically through Square.",
  },
  {
    q: "What's the difference between a detail and a car wash?",
    a: "A car wash is a quick clean. A detail is a thorough restoration — we treat every surface, condition leather, decontaminate paint, and leave your car in near-showroom condition. Night and day difference.",
  },
  {
    q: "Do I need to be home during the appointment?",
    a: "Not necessarily. As long as we have access to the vehicle and a nearby power/water source (or just the vehicle for waterless services), you're free to go about your day.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit/debit cards, Apple Pay, Google Pay, and cash through Square. Payment is collected at time of service.",
  },
  {
    q: "How often should I get my car detailed?",
    a: "For most daily drivers, a full detail every 3–6 months keeps things in great shape. Ceramic-coated vehicles can stretch longer between details since the coating repels contaminants.",
  },
  {
    q: "What is ceramic coating and is it worth it?",
    a: "Ceramic coating is a liquid polymer bonded to your paint that creates a hard, hydrophobic protective layer. It makes washing easier, protects against UV, minor scratches, and chemical damage. For anyone who cares about their vehicle's long-term appearance, it's absolutely worth it.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes. Bizzy Details is fully licensed and insured. Your vehicle is protected on every job.",
  },
];

function AccordionItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (open) {
      gsap.fromTo(el, { height: 0, opacity: 0 }, { height: "auto", opacity: 1, duration: 0.35, ease: "power3.out" });
    } else {
      gsap.to(el, { height: 0, opacity: 0, duration: 0.25, ease: "power3.in" });
    }
  }, [open]);

  return (
    <div className="faq-item border-b" style={{ borderColor: "#1a1a1a" }}>
      <button
        className="w-full flex items-center justify-between py-6 text-left gap-6"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="flex items-center gap-5">
          <span
            className="font-black italic flex-shrink-0 w-8"
            style={{ fontFamily: "var(--font-display)", fontSize: "1rem", color: "#1E5EFF" }}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className="font-bold uppercase"
            style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", letterSpacing: "0.04em", color: "#F2F2F0" }}
          >
            {q}
          </span>
        </div>
        <svg
          width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          className="flex-shrink-0 transition-transform duration-300"
          style={{ color: "#1E5EFF", transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </button>
      <div ref={bodyRef} style={{ height: 0, overflow: "hidden", opacity: 0 }}>
        <p className="pb-6 pl-13" style={{ paddingLeft: "52px", color: "#808080", lineHeight: 1.75, fontSize: "0.95rem" }}>
          {a}
        </p>
      </div>
    </div>
  );
}

export function FaqAccordion() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".faq-item",
        { opacity: 0, x: -24 },
        {
          opacity: 1, x: 0, duration: 0.6, ease: "power3.out", stagger: 0.06,
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
        }
      );
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 border-b" style={{ background: "#050505", borderColor: "#1a1a1a" }} id="faq">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-16">
          <div
            className="flex items-center gap-3 mb-4"
            style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: "#1E5EFF" }}
          >
            <span style={{ width: 24, height: 2, background: "#1E5EFF", display: "inline-block", flexShrink: 0 }} />
            FAQ
          </div>
          <h2
            className="font-black italic uppercase"
            style={{ fontFamily: "var(--font-display)", fontSize: "clamp(3rem,6vw,5rem)", lineHeight: 0.9, color: "#F2F2F0" }}
          >
            Got Questions?
          </h2>
        </div>
        <div>
          {FAQS.map((item, i) => (
            <AccordionItem key={i} {...item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
